import { Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { FAVICONS, CURRENT_FAVICON_SVG } from "../lib/faviconOptions";

const FONTS = [
  {
    id: "a",
    name: "Fraunces",
    note: "Modern serif with warmth & personality. Slightly playful, very on-trend for premium food.",
    family: "'Fraunces', serif",
    archnaWeight: 700,
    kitchenWeight: 500,
    archnaItalic: false,
  },
  {
    id: "b",
    name: "Cormorant Garamond",
    note: "Delicate, refined, classic-luxury — almost editorial.",
    family: "'Cormorant Garamond', serif",
    archnaWeight: 700,
    kitchenWeight: 500,
    archnaItalic: true,
  },
  {
    id: "c",
    name: "DM Serif Display",
    note: "Bolder, architectural, confident. High-impact masthead feel.",
    family: "'DM Serif Display', serif",
    archnaWeight: 400,
    kitchenWeight: 400,
    archnaItalic: false,
  },
  {
    id: "d",
    name: "Yeseva One",
    note: "Dramatic condensed serif. Strong, dignified, slightly vintage.",
    family: "'Yeseva One', serif",
    archnaWeight: 400,
    kitchenWeight: 400,
    archnaItalic: false,
  },
  {
    id: "e",
    name: "Marcellus",
    note: "Calligraphic, slightly Roman. Elegant and quiet.",
    family: "'Marcellus', serif",
    archnaWeight: 400,
    kitchenWeight: 400,
    archnaItalic: false,
  },
  {
    id: "f",
    name: "Caveat (script) + Lato",
    note: "Handwritten 'Archna's' (like a real cook's name on a tiffin) + clean Lato 'Kitchen'.",
    family: "'Caveat', cursive",
    archnaWeight: 700,
    kitchenWeight: 700,
    archnaItalic: false,
    secondaryFamily: "'Lato', sans-serif",
  },
  {
    id: "current",
    name: "Playfair Display (current)",
    note: "What's live right now.",
    family: "'Playfair Display', serif",
    archnaWeight: 600,
    kitchenWeight: 600,
    archnaItalic: false,
    isCurrent: true,
  },
];

function BrandSample({ font }) {
  const fam = font.family;
  const kFam = font.secondaryFamily || font.family;
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full border-2 border-[#c9a84c] flex items-center justify-center bg-[#0a1530] shrink-0">
        <span
          style={{
            fontFamily: fam,
            color: "#c9a84c",
            fontSize: "1.25rem",
            fontWeight: font.archnaWeight,
            lineHeight: 1,
          }}
        >
          A
        </span>
      </div>
      <div className="leading-none">
        <div
          style={{
            fontFamily: fam,
            fontWeight: font.archnaWeight,
            fontStyle: font.archnaItalic ? "italic" : "normal",
            fontSize: "1.5rem",
            color: "white",
            letterSpacing: "0.005em",
          }}
        >
          Archna's{" "}
          <span
            style={{
              fontFamily: kFam,
              fontWeight: font.kitchenWeight,
              color: "#c9a84c",
              fontStyle: "normal",
            }}
          >
            Kitchen
          </span>
        </div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mt-1">
          Pure · Home-Cooked · Delhi
        </div>
      </div>
    </div>
  );
}

function HeroSample({ font }) {
  return (
    <div
      style={{
        fontFamily: font.family,
        fontWeight: font.archnaWeight,
        fontStyle: font.archnaItalic ? "italic" : "normal",
        lineHeight: 1.05,
        letterSpacing: "-0.01em",
      }}
      className="text-white text-3xl md:text-4xl"
    >
      Real Food.
      <br />
      Pure Ingredients.
      <br />
      <span className="text-[#c9a84c] italic">Delivered.</span>
    </div>
  );
}

export default function PreviewPage() {
  return (
    <div className="bg-[#faf8f5] pt-[100px] pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#1a2e5a] text-sm font-bold tracking-wide mb-6">
          <ArrowLeft size={16} /> Back to live site
        </Link>
        <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-3">
          Internal · Design Review
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-[#1a2e5a] leading-tight mb-3">
          Brand font & favicon — pick your favourite.
        </h1>
        <p className="font-body text-slate-600 max-w-2xl mb-12">
          Each row shows the navbar logotype + a hero-style headline rendered
          in that font. Each tile shows a candidate favicon at the size it
          will actually appear in the browser tab. Tell me a font letter (a–f)
          and a favicon number (1–6) and I'll wire it in.
        </p>

        {/* FONTS */}
        <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1a2e5a] mb-6">
          1 — Brand-name fonts
        </h2>
        <div className="space-y-5 mb-20">
          {FONTS.map((f) => (
            <div
              key={f.id}
              className={`relative rounded-3xl overflow-hidden border-2 ${
                f.isCurrent
                  ? "border-[#c9a84c]/60"
                  : "border-[#c9a84c]/25"
              }`}
              data-testid={`font-row-${f.id}`}
            >
              {/* Top: dark navbar context */}
              <div className="bg-[#1a2e5a] px-6 md:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
                <BrandSample font={f} />
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#c9a84c] font-bold">
                  Option {f.id.toUpperCase()}
                  {f.isCurrent ? " · Current" : ""}
                </div>
              </div>
              {/* Bottom: cream hero context */}
              <div className="bg-[#0a1530] px-6 md:px-8 py-7 grid md:grid-cols-3 gap-6 items-center">
                <div className="md:col-span-2">
                  <HeroSample font={f} />
                </div>
                <div>
                  <div className="font-display text-lg text-[#c9a84c] mb-1">
                    {f.name}
                  </div>
                  <div className="text-sm text-white/70 leading-relaxed">
                    {f.note}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* FAVICONS */}
        <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1a2e5a] mb-6">
          2 — Favicon options
        </h2>
        <p className="text-sm text-slate-600 mb-6">
          Browser tab favicons render at 16-32px. The small preview on the
          right of each tile shows roughly that size — clarity at small sizes
          matters most.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {/* Current */}
          <div className="rounded-3xl border-2 border-[#c9a84c]/60 overflow-hidden bg-white" data-testid="fav-row-current">
            <div className="p-6 flex items-center gap-5">
              <div
                className="w-20 h-20 rounded-2xl shrink-0"
                dangerouslySetInnerHTML={{ __html: CURRENT_FAVICON_SVG }}
              />
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#c9a84c] font-bold mb-1">
                  Current
                </div>
                <div className="font-display text-lg text-[#1a2e5a] font-semibold">
                  Gold A in circle
                </div>
                <div className="text-sm text-slate-600 mt-1">What's live now.</div>
              </div>
            </div>
            <div className="px-6 py-4 bg-[#faf8f5] border-t border-[#c9a84c]/20 flex items-center gap-3">
              <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500">Tab size:</span>
              <div className="w-4 h-4" dangerouslySetInnerHTML={{ __html: CURRENT_FAVICON_SVG }} />
              <div className="w-6 h-6" dangerouslySetInnerHTML={{ __html: CURRENT_FAVICON_SVG }} />
              <div className="w-8 h-8" dangerouslySetInnerHTML={{ __html: CURRENT_FAVICON_SVG }} />
            </div>
          </div>

          {FAVICONS.map((f) => (
            <div
              key={f.id}
              className="rounded-3xl border-2 border-[#c9a84c]/25 overflow-hidden bg-white card-lift"
              data-testid={`fav-row-${f.id}`}
            >
              <div className="p-6 flex items-center gap-5">
                <div
                  className="w-20 h-20 rounded-2xl shrink-0"
                  dangerouslySetInnerHTML={{ __html: f.svg }}
                />
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#c9a84c] font-bold mb-1">
                    Option {f.id}
                  </div>
                  <div className="font-display text-lg text-[#1a2e5a] font-semibold">
                    {f.name}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">{f.desc}</div>
                </div>
              </div>
              <div className="px-6 py-4 bg-[#faf8f5] border-t border-[#c9a84c]/20 flex items-center gap-3">
                <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500">Tab size:</span>
                <div className="w-4 h-4" dangerouslySetInnerHTML={{ __html: f.svg }} />
                <div className="w-6 h-6" dangerouslySetInnerHTML={{ __html: f.svg }} />
                <div className="w-8 h-8" dangerouslySetInnerHTML={{ __html: f.svg }} />
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl bg-[#1a2e5a] text-white p-6 md:p-8 flex items-start gap-4">
          <Check size={22} className="text-[#c9a84c] mt-1 shrink-0" />
          <div>
            <div className="font-display text-xl font-semibold mb-1">
              Ready to choose?
            </div>
            <div className="text-white/75 text-sm">
              Reply with your picks like <em>"font: c, favicon: 4"</em> and
              I'll apply them across the live site immediately.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
